import { useState, useEffect, useRef } from 'react';
import { BlockButton, Breakpoints, Icon, IconDefinitions, ODataGrid, ResponsiveGridColumn } from '../../../../components';
import CTEyeService from '../../../../services/CTEyeService';
import { UrlService } from '../../../../services';
import { useParams, useLocation } from 'react-router-dom';
import { GridCellProps, GridRowClickEvent } from '@progress/kendo-react-grid';
import { DataFileInfo } from '../../../../data/entities';
import { useSharedState } from '../../../../app/sharedProperty';
import { POLL_DATA_FILES_MS } from '../../helper/pollingConfig';
import { CreateInspectionModal } from '..';
import { LIVE_WRITE_THRESHOLD_MS } from '../../helper'
import { formatDateTime } from '../../../../app/dateHelpers';

export function DataFilesGrid() {
  const urlParams = useParams();
  const id = UrlService.getParamValue("id", urlParams) ?? "0";
  const service = new CTEyeService();

  const location = useLocation();
  const cardRef = useRef<HTMLDivElement>(null);

  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedFile, setSelectedFile] = useState<DataFileInfo | null>(null);
  const [pendingScroll, setPendingScroll] = useState(false);
  const showCreateInspectionModalState = useSharedState(false);

  useEffect(() => {
    const openFile = (location.state as any)?.openFile as DataFileInfo | undefined;
    if (openFile) {
      setSelectedFile(openFile);
      setPendingScroll(true);
      window.history.replaceState({}, '');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (pendingScroll) {
      cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      showCreateInspectionModalState.setter(true);
      setPendingScroll(false);
    }
  }, [pendingScroll]);

  useEffect(() => {
    const interval = setInterval(() => {
      setRefreshKey(prev => prev + 1);
    }, POLL_DATA_FILES_MS);
    return () => clearInterval(interval);
  }, [id]);

  const getData = async (oDataQuery: string) => {
    const cutoff = new Date(Date.now() - LIVE_WRITE_THRESHOLD_MS).toISOString();
    const filter = `lastWriteTime lt ${cutoff}`;
    const separator = oDataQuery.includes('$filter') ? ' and ' : '&$filter=';
    return await service.getClientDataFilesOData(id, `${oDataQuery}${separator}${filter}`);
  };

  const handleRowClick = (e: GridRowClickEvent) => {
    const file = e.dataItem as DataFileInfo;
    setSelectedFile(file);
    showCreateInspectionModalState.setter(true);
  };

  const handleRefresh = () => setRefreshKey(prev => prev + 1);

  const handleInspectionCreated = () => {
    setRefreshKey(prev => prev + 1);
    setSelectedFile(null);
  };

  const fileNameCellBuilder = (props: GridCellProps) => {
    const file = props.dataItem as DataFileInfo;
    return (
      <td className="px-3 py-3">
        <div className="d-flex align-items-center" style={{ pointerEvents: 'none' }}>
          <Icon iconName={IconDefinitions.fileAlt} className="me-2 text-primary" />
          <span className="fw-semibold">{file.fileName}</span>
        </div>
      </td>
    );
  };

  const pathCellBuilder = (props: GridCellProps) => {
    const file = props.dataItem as DataFileInfo;
    return (
      <td className="px-3 py-3">
        <code className="small text-muted" style={{ pointerEvents: 'none' }}>
          {file.path}
        </code>
      </td>
    );
  };

  const fileDateCellBuilder = (props: GridCellProps) => {
    const file = props.dataItem as DataFileInfo;
    const raw = file.fileDate ?? file.lastWriteTime;
    return (
      <td className="text-secondary small">
        {raw ? formatDateTime(new Date(raw)) : "—"}
      </td>
    );
  };

  const sizeCellBuilder = (props: GridCellProps) => {
    const file = props.dataItem as DataFileInfo;
    const sizeMB = (file.sizeBytes / 1024 / 1024).toFixed(2);
    return (
      <td className="px-3 py-3 text-center">{sizeMB} MB</td>
    );
  };

  return (
    <>
      {/* Page Header */}
      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-2 mb-3">
        <div>
          <h5 className="mb-0 fw-semibold d-flex align-items-center">
            <Icon iconName={IconDefinitions.fileAlt} className="text-primary" />
            Available Files
          </h5>
          <small className="text-muted d-none d-sm-block">
            Click a row to create an inspection &middot; Auto-refreshes every {POLL_DATA_FILES_MS / 1000}s
          </small>
          <small className="text-muted d-block d-sm-none">
            Tap a row to inspect &middot; Refreshes every {POLL_DATA_FILES_MS / 1000}s
          </small>
        </div>
        <BlockButton onClick={handleRefresh} className="btn-sm">
          <Icon iconName={IconDefinitions.refresh} className="me-1" />
          Refresh
        </BlockButton>
      </div>
      <div ref={cardRef} className="card shadow-sm border-0 mb-4">
        <div className="card-body p-0">
          <ODataGrid
            key={refreshKey}
            getData={getData}
            onRowClick={handleRowClick}
            pageSize={20}
            pageable={{ buttonCount: 5 }}
            sortable={true}
            filterable={true}
            // sort={[{ field: "fileName", dir: "asc" }]}
          >
            <ResponsiveGridColumn
              title="File Name"
              field="fileName"
              sortable={true}
              filter="text"
              cell={fileNameCellBuilder}
            />
            <ResponsiveGridColumn
              title="Size"
              field="sizeBytes"
              sortable={true}
              filterable={false}
              breakpoint={Breakpoints.md}
              cell={sizeCellBuilder}
              width="200"
            />
            <ResponsiveGridColumn
              title="Completed Date"
              field="fileDate"
              sortable={true}
              filterable={false}
              breakpoint={Breakpoints.sm}
              cell={fileDateCellBuilder}
              width="200px"
            />
          </ODataGrid>
        </div>
      </div>

      <CreateInspectionModal
        showState={showCreateInspectionModalState}
        clientId={id}
        file={selectedFile}
        onSuccess={handleInspectionCreated}
      />
    </>
  );
}

export default DataFilesGrid;
